import {
  getPermissionUtils,
  NotificationType,
  type PermissionUtils,
} from '@tyro/api';
import { getInfoRequestFormList } from '@tyro/info-requests/api/form-list';
import { useEffect, useState } from 'react';
import type { ReturnTypeFromNotifications } from '../api/notifications/list';

async function getNotificationUrl(
  notificationMeta: ReturnTypeFromNotifications['recipients'][number]['metaData'],
  permissions: PermissionUtils,
) {
  switch (notificationMeta?.notificationType) {
    case NotificationType.Mail: {
      const { threadMessageId } = notificationMeta;
      return threadMessageId
        ? `/mail/inbox/view/${threadMessageId}?from=notification`
        : '/mail/inbox';
    }
    case NotificationType.InfoRequest: {
      const { formId } = notificationMeta;

      const { forms_listInformationRequestForms: requestsData = [] } =
        await getInfoRequestFormList({});

      const currentRequest = requestsData.find(
        ({ id }) => id.objectId === formId,
      );

      if (!currentRequest) return '/info-requests';
      const { provider, forPartyId, objectId } = currentRequest.id;
      return `/info-requests/view?provider=${provider}&forPartyId=${
        forPartyId ?? 0
      }&objectId=${objectId ?? 0}`;
    }
    case NotificationType.Substitution: {
      return `/calendar?date=${notificationMeta.substitutionDate}`;
    }
    case NotificationType.SchoolActivityUpdated: {
      return `/school-activity/school-activity/${notificationMeta.schoolActivityId}/students?viewStatus=true`;
    }
    case NotificationType.SchoolActivityCreated: {
      return `/school-activity/school-activity/${notificationMeta.schoolActivityId}`;
    }
    case NotificationType.Assessment: {
      const { studentPartyId, assessmentId } = notificationMeta;
      return `people/students/${studentPartyId}/assessment?assessmentId=${assessmentId}`;
    }
    case NotificationType.Fee: {
      const { studentPartyId, feeId } = notificationMeta;
      return `/fees/list?tab=DUE&feeId=${feeId}&studentPartyId=${studentPartyId}`;
    }
    case NotificationType.FeePaymentFailed: {
      return '/fees/saved-cards';
    }
    case NotificationType.FeePaymentUpcoming: {
      const { studentPartyId, feeId } = notificationMeta;
      return `/fees/list?tab=SCHEDULED&feeId=${feeId}&studentPartyId=${studentPartyId}`;
    }
    case NotificationType.Timetable: {
      if (permissions.hasPermission('ps:1:calendar:view_calendar')) {
        const { eventStartTime } = notificationMeta;

        const searchParamsPath = [['date', eventStartTime]]
          .filter(([_, value]) => value)
          .map(([key, value]) => `${key}=${value}`)
          .reduce((acc, param) => `${acc}${param}&`, '?');

        return `/calendar/personal${searchParamsPath}`;
      }

      return null;
    }
    case NotificationType.Highlight: {
      const { studentPartyId, highlightId, highlightCommentId } =
        notificationMeta;

      const highlightIdUrl = `/people/students/${studentPartyId}/timeline?highlightId=${highlightId}`;

      if (highlightCommentId) {
        return `${highlightIdUrl}&highlightCommentId=${highlightCommentId}`;
      }

      return highlightIdUrl;
    }
    case NotificationType.StaffAbsenceRequest: {
      const { staffAbsenceRequestId } = notificationMeta;
      return `/absence-requests?requestId=${staffAbsenceRequestId}`;
    }
    default:
      return null;
  }
}

async function getNotificationUrls(
  notifications: ReturnTypeFromNotifications[],
) {
  const permissions = await getPermissionUtils();

  const linkPromises = notifications.map(async (notification) => {
    const url = await getNotificationUrl(
      notification.recipients[0].metaData,
      permissions,
    );
    return {
      ...notification,
      url,
    };
  });

  return Promise.all(linkPromises);
}

export function useNotificationLinks(
  notifications: ReturnTypeFromNotifications[] | undefined,
) {
  const [notificationUrls, setNotificationUrls] = useState<Map<string, string>>(
    new Map(),
  );

  useEffect(() => {
    getNotificationUrls(notifications ?? []).then((urls) => {
      const newMap = new Map(
        urls
          .filter(({ url }) => !!url)
          .map((notification) => [
            notification.notificationId,
            notification.url,
          ]),
      );
      setNotificationUrls(newMap as Map<string, string>);
    });
  }, [notifications]);

  return notificationUrls;
}
